import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import ServiceDialog, { SERVICES } from "../components/ServiceDialog";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, inr, fileUrl, fmtErr } from "../api";
import { useAuth, homeFor } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Stamp, Landmark, Scale, Building2, Banknote, UploadCloud, ScanSearch,
  GitCompareArrows, UserCheck, FileCheck2, MapPin, Phone, Mail, ArrowRight, Users, Eye,
  ClipboardList, CheckCircle2, Loader2, Layers, Workflow, SearchCheck,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/36665297/pexels-photo-36665297.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FLOW = ["Verify", "Documents", "Loan", "Registration", "Completed"];
const AI_STEPS = [
  { icon: UploadCloud, title: "Upload", desc: "Submit the property documents required for the verification process." },
  { icon: ScanSearch, title: "Extract", desc: "PropLoX identifies relevant information — names, property identifiers and registration details." },
  { icon: GitCompareArrows, title: "Check", desc: "The system compares information across the available documents and flags inconsistencies." },
  { icon: UserCheck, title: "Review", desc: "Where professional review is required, the findings can be reviewed by a legal professional." },
  { icon: FileCheck2, title: "Report", desc: "Receive a structured report showing the findings, identified issues and suggested next steps." },
];
const WORK_STEPS = [
  { icon: SearchCheck, title: "Verify", desc: "Review available property documents and identify information that needs attention." },
  { icon: ClipboardList, title: "Prepare", desc: "Organize the documents and requirements needed for the next stage." },
  { icon: Workflow, title: "Process", desc: "Coordinate relevant documentation, loan or registration requirements." },
  { icon: Eye, title: "Review", desc: "Complete professional review where the transaction requires it." },
  { icon: CheckCircle2, title: "Complete", desc: "Track the remaining steps until the transaction reaches completion." },
];
const PEOPLE = [
  { role: "Buyer", desc: "Provides information and documents and tracks the transaction." },
  { role: "Developer", desc: "Manages property and buyer documentation across transactions." },
  { role: "Lawyer", desc: "Reviews documents and provides professional legal judgement where required." },
  { role: "Loan Team", desc: "Coordinates property documents and loan-related requirements." },
  { role: "Registration Team", desc: "Coordinates the documentation and steps required towards registration." },
];
const KA_DOCS = ["Sale Deed", "RTC (Pahani)", "Mutation Records", "Encumbrance Certificate", "Khata Documents", "Property Tax Documents"];
const WHY = [
  { icon: Workflow, title: "Structured Workflow", desc: "Keep important transaction steps organized instead of handling everything separately." },
  { icon: FileText, title: "Document Visibility", desc: "Keep relevant documents and information connected to the transaction." },
  { icon: ScanSearch, title: "AI-Assisted Analysis", desc: "Use technology to help identify relevant information and potential inconsistencies in property documents." },
  { icon: UserCheck, title: "Human Review Where Required", desc: "Professional legal review can be part of the process when the transaction requires it." },
  { icon: Layers, title: "Transaction Tracking", desc: "Know what has been completed and what still needs attention." },
];
const BUSINESS = [
  { icon: Building2, name: "Developers", desc: "Buyer onboarding, document collection, legal verification, and loan & registration tracking across multiple properties." },
  { icon: Scale, name: "Law Firms", desc: "Organize client documents, review workflow and transaction status in one place." },
  { icon: Banknote, name: "Banks & NBFCs", desc: "Structured property-document workflows and coordination around transactions." },
];

function Section({ id, eyebrow, title, intro, children, dark = false, className = "" }) {
  return (
    <section id={id} className={`py-16 lg:py-24 ${dark ? "bg-slate-900 text-white" : ""} ${className}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {eyebrow && <p className={`eyebrow ${dark ? "text-blue-400" : ""}`}>{eyebrow}</p>}
        <h2 className={`mt-3 max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
        {intro && <p className={`mt-4 max-w-3xl text-base leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{intro}</p>}
        {children}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [slide, setSlide] = useState(0);
  const [dialog, setDialog] = useState(null); // null | { mode: "service"|"business"|"contact", service?: string }
  const [contact, setContact] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const slides = useMemo(() => {
    const fromProjects = properties
      .map((p) => ({ img: fileUrl(p.image_url), name: p.name, location: `${p.location}, ${p.city}` }))
      .filter((s) => s.img);
    return fromProjects.length ? fromProjects : [{ img: HERO_IMG, name: "Property Transactions", location: "Karnataka" }];
  }, [properties]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  const openService = () => setDialog({ mode: "service", service: "" });
  const openServiceFor = (s) => setDialog({ mode: "service", service: s });
  const sendContact = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { kind: "contact", ...contact });
      toast.success("Message sent. Our team will reach out shortly.");
      setContact({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <PublicNav onGetService={openService} />
      <ServiceDialog open={dialog !== null} onOpenChange={() => setDialog(null)} mode={dialog?.mode || "service"} initialService={dialog?.service || ""} />

      {/* ============ HERO ============ */}
      <section className="hero-radial">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.4rem]" data-testid="hero-headline">
              Complete Property Transactions. <span className="text-blue-600">One Platform.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-subtext">
              From property verification to documentation, loan assistance and registration — manage your
              property transaction with PropLoX.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" data-testid="hero-get-service-button" onClick={openService}>Get a Service</Button>
              <Button size="lg" variant="outline" data-testid="hero-business-button"
                onClick={() => document.getElementById("business")?.scrollIntoView({ behavior: "smooth" })}>For Business</Button>
            </div>
            <p className="mt-4 text-sm text-slate-500">Choose the service you need. Keep the important steps, documents and progress connected.</p>
            <div className="mt-7 flex flex-wrap items-center gap-y-2" data-testid="hero-flow">
              {FLOW.map((f, i) => (
                <span key={f} className="flex items-center">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${i === 4 ? "bg-blue-600 text-white" : "border border-slate-300 bg-white text-slate-700"}`}>{f}</span>
                  {i < FLOW.length - 1 && <ArrowRight className="mx-1.5 h-3.5 w-3.5 text-slate-300" />}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-2xl" data-testid="hero-slider">
              {slides.map((s, i) => (
                <img key={i} src={s.img} alt={s.name} data-testid={`hero-slide-${i}`}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`} />
              ))}
              {slides.length > 1 && (
                <div className="absolute left-4 top-4 max-w-[70%] truncate rounded-full bg-slate-950/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur" data-testid="hero-slide-caption">
                  {slides[slide].name} · {slides[slide].location}
                </div>
              )}
              {slides.length > 1 && (
                <div className="absolute bottom-4 right-4 flex items-center gap-1.5">
                  {slides.map((_, i) => (
                    <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-slide-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROBLEM ============ */}
      <Section id="problem" eyebrow="The Problem" title="Property transactions involve more than one step"
        className="border-y border-slate-200 bg-white"
        intro="A property transaction can involve document checks, legal review, paperwork, loan requirements and registration. These steps are often handled by different people, documents and communication channels. PropLoX brings the relevant services and transaction progress together — so you can understand what has been done, what needs attention and what comes next.">
        <p className="mt-6 rounded-xl border border-blue-100 bg-blue-50/60 px-5 py-4 text-sm font-medium text-blue-900">
          The goal is simple: make property transactions easier to understand and easier to manage.
        </p>
      </Section>

      {/* ============ SOLUTION ============ */}
      <Section id="solution" eyebrow="The PropLoX Solution" title="One place to manage the transaction"
        intro="PropLoX provides property-related services through one platform. Start with the service you need, keep your documents and tasks organized, and track the next steps of the transaction. We use technology to make document and workflow management easier, with professional support where required.">
        <Button className="mt-6" variant="outline" data-testid="explore-proplox-button"
          onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}>
          Explore PropLoX <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Section>

      {/* ============ SERVICES ============ */}
      <Section id="services" eyebrow="Services" title="Services for every stage of your property transaction"
        className="border-y border-slate-200 bg-white"
        intro="Choose the service you need. You don't have to use every service to get started.">
        <p className="mt-8 text-xs font-bold uppercase tracking-widest text-slate-400">For Individuals</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.id} className="card-lift flex flex-col rounded-xl border border-slate-200 bg-white p-6" data-testid={`service-card-${s.id.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <s.icon className="h-7 w-7 text-blue-600" />
              <h3 className="mt-3 text-base font-semibold text-slate-900">{s.id}</h3>
              <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              <button onClick={() => openServiceFor(s.id)} className="mt-4 flex items-center gap-1 text-sm font-semibold text-blue-600 hover:gap-2 transition-all" data-testid={`service-get-${s.id.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                Get Service <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex flex-col justify-center rounded-xl border border-blue-200 bg-blue-600 p-6 text-white" data-testid="service-card-verification-highlight">
            <ShieldCheck className="h-7 w-7" />
            <h3 className="mt-3 text-base font-semibold">Start with Property Verification</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-blue-100">Upload your documents and get a structured, evidence-linked verification report.</p>
            <button onClick={() => openServiceFor("Property Verification")} className="mt-4 flex items-center gap-1 text-sm font-semibold text-white hover:gap-2 transition-all" data-testid="service-get-highlight">
              Start Now <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <p className="mt-10 text-xs font-bold uppercase tracking-widest text-slate-400">For Business</p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {BUSINESS.map((b) => (
            <div key={b.name} className="rounded-xl border border-slate-200 bg-slate-50 p-6" data-testid={`business-mini-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <b.icon className="h-6 w-6 text-blue-600" />
              <h3 className="mt-3 text-base font-semibold text-slate-900">{b.name}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{b.desc}</p>
            </div>
          ))}
        </div>
        <Button className="mt-6" variant="outline" data-testid="explore-business-button" onClick={() => setDialog({ mode: "business", service: "" })}>
          Explore PropLoX for Business <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Section>

      {/* ============ AI VERIFICATION ============ */}
      <Section id="ai-verification" dark eyebrow="AI Verification" title="AI-assisted property document analysis"
        intro="PropLoX uses AI to analyse submitted property documents, extract relevant information and identify inconsistencies that may require attention. The output is structured so that findings can be reviewed and acted upon.">
        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">What the system can help identify</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-200">
              {["Relevant names and property details", "Property and survey identifiers", "Registration details",
                "Information consistency across available documents", "Potential discrepancies requiring further attention"].map((t) => (
                <li key={t} className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />{t}</li>
              ))}
            </ul>
            <p className="mt-6 rounded-xl border border-slate-700 bg-slate-800/60 p-4 text-xs leading-relaxed text-slate-300" data-testid="ai-disclaimer">
              <b>Important:</b> AI assists the analysis. It does not replace professional legal judgement. Where
              professional review is required, the findings can be reviewed by a legal professional.
            </p>
            <Button className="mt-6" data-testid="explore-verification-button" onClick={() => openServiceFor("Property Verification")}>
              Explore Property Verification <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">From documents to a structured report</p>
            <div className="mt-4 space-y-3" id="how-it-works">
              {AI_STEPS.map((s, i) => (
                <div key={s.title} className="flex items-start gap-4 rounded-xl border border-slate-700/80 bg-slate-800/40 p-4" data-testid={`ai-step-${i + 1}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600/20 font-mono text-sm font-bold text-blue-400">{i + 1}</span>
                  <div>
                    <p className="text-sm font-semibold text-white">{s.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{s.desc}</p>
                  </div>
                  <s.icon className="ml-auto mt-1 h-4 w-4 shrink-0 text-slate-500" />
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
              The verification output depends on the documents and information available for the transaction.
              Automated analysis should not be treated as a substitute for professional legal advice.
            </p>
          </div>
        </div>
      </Section>

      {/* ============ WHAT YOU RECEIVE ============ */}
      <Section id="report" eyebrow="What You Receive" title="Know what was found. Know what needs attention."
        intro="A PropLoX verification output is designed to make the available property information easier to understand.">
        <div className="mt-8 grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              { icon: ClipboardList, t: "Property Information", d: "Relevant information identified from the submitted documents." },
              { icon: SearchCheck, t: "Document Observations", d: "Inconsistencies or missing information identified during the analysis." },
              { icon: ShieldCheck, t: "Issues Requiring Attention", d: "Items that may require additional documents, clarification or professional review." },
              { icon: ArrowRight, t: "Next Steps", d: "A structured view of what needs to happen next." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-slate-200 bg-white p-5">
                <c.icon className="h-5 w-5 text-blue-600" />
                <p className="mt-2.5 text-sm font-semibold text-slate-900">{c.t}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{c.d}</p>
              </div>
            ))}
            <a href="/sample-report.pdf" target="_blank" rel="noreferrer" className="sm:col-span-2" data-testid="view-sample-report-link">
              <Button variant="outline" className="w-full sm:w-auto">View a Sample Report <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </a>
          </div>
          <a href="/sample-report.pdf" target="_blank" rel="noreferrer" className="group block" data-testid="sample-report-image-link">
            <img src="/evidence-report.png" alt="Actual PropLoX verification report — first page"
              className="w-full rounded-xl border border-slate-300 shadow-xl transition-transform duration-300 group-hover:-translate-y-1" data-testid="sample-report-image" />
            <p className="mt-2 text-center text-xs text-slate-400">An actual PropLoX verification report (sample data). Click to open the full PDF.</p>
          </a>
        </div>
      </Section>

      {/* ============ TRANSACTION WORKSPACE ============ */}
      <Section id="workspace" dark eyebrow="One Transaction Workspace" title="From verification to completion"
        intro="A property transaction involves more than checking documents. PropLoX is designed to keep the important stages connected — documents, tasks, status and communication around the same property transaction.">
        <div className="mt-8 flex flex-wrap items-center gap-y-3" data-testid="transaction-flow">
          {["Buyer / Developer", "Property Verification", "Documentation", "Loan Assistance", "Registration", "Transaction Completed"].map((s, i, arr) => (
            <span key={s} className="flex items-center">
              <span className={`rounded-full px-4 py-2 text-xs font-semibold ${i === arr.length - 1 ? "bg-blue-600 text-white" : "border border-slate-600 bg-slate-800/60 text-slate-200"}`}>{s}</span>
              {i < arr.length - 1 && <ArrowRight className="mx-2 h-4 w-4 text-slate-600" />}
            </span>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm text-slate-300">
          Instead of managing every step separately, keep the transaction progress visible in one place.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {WORK_STEPS.map((s) => (
            <div key={s.title} className="rounded-xl border border-slate-700/80 bg-slate-800/40 p-5" data-testid={`work-step-${s.title.toLowerCase()}`}>
              <s.icon className="h-5 w-5 text-blue-400" />
              <p className="mt-2.5 text-sm font-semibold text-white">{s.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ PEOPLE ============ */}
      <Section id="people" eyebrow="The People Involved" title="One transaction. Multiple stakeholders."
        intro="PropLoX is designed to connect the workflow around the transaction — not replace the professionals involved.">
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {PEOPLE.map((p) => (
            <div key={p.role} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`person-${p.role.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Users className="h-5 w-5 text-blue-600" />
              <p className="mt-2.5 text-sm font-semibold text-slate-900">{p.role}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ KARNATAKA ============ */}
      <Section id="karnataka" eyebrow="Built for Karnataka" title="Starting with Karnataka property transactions"
        className="border-y border-slate-200 bg-white"
        intro="PropLoX is initially focused on Karnataka property transactions and the documents, workflows and professional coordination involved in them. Documents may include:">
        <div className="mt-6 flex flex-wrap gap-2" data-testid="karnataka-documents">
          {KA_DOCS.map((d) => (
            <span key={d} className="rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 text-xs font-semibold text-blue-700">{d}</span>
          ))}
        </div>
      </Section>

      {/* ============ WHO IT'S FOR ============ */}
      <Section id="who" eyebrow="Who It's For" title="Built for the people involved in property transactions">
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { t: "Property Buyers", d: "Understand the documents, checks and next steps involved in your property transaction.", cta: "Get a Service", act: openService },
            { t: "Developers", d: "Keep buyer documentation and transaction progress organized across multiple transactions.", cta: "For Developers", act: () => setDialog({ mode: "business", service: "" }) },
            { t: "Law Firms", d: "Organize client documents, review tasks and transaction information in one workflow.", cta: "For Law Firms", act: () => setDialog({ mode: "business", service: "" }) },
            { t: "Banks & NBFCs", d: "Make property-document coordination easier across the transaction process.", cta: "Explore Business", act: () => setDialog({ mode: "business", service: "" }) },
          ].map((c) => (
            <div key={c.t} className="flex flex-col rounded-xl border border-slate-200 bg-white p-6" data-testid={`who-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="text-base font-semibold text-slate-900">For {c.t}</p>
              <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-600">{c.d}</p>
              <button onClick={c.act} className="mt-4 flex items-center gap-1 text-sm font-semibold text-blue-600 hover:gap-2 transition-all" data-testid={`who-cta-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {c.cta} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ WHY PROPLOX + TRUST ============ */}
      <Section id="why" eyebrow="Why PropLoX" title="Designed around the transaction, not just the document"
        className="border-y border-slate-200 bg-white">
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <w.icon className="h-5 w-5 text-blue-600" />
              <p className="mt-2.5 text-sm font-semibold text-slate-900">{w.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-600">{w.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-900 p-8 text-white sm:p-10" data-testid="trust-block">
          <p className="eyebrow text-blue-400">Trust</p>
          <h3 className="mt-2 text-xl font-bold sm:text-2xl">Technology should make the process clearer — not make bigger promises.</h3>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300">
            Property transactions involve important documents, legal requirements and financial decisions. PropLoX combines
            technology, structured workflows and professional support to help manage these transactions. We clearly separate
            automated document analysis from professional legal review, so customers know what the system has done and what
            still requires expert attention.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { t: "Clear about technology", d: "We explain what automated analysis does and where professional review is required." },
              { t: "Clear about the information", d: "Our analysis is based on the documents and information available for the transaction." },
              { t: "Clear about uncertainty", d: "Potential inconsistencies or missing information are presented for further attention — not hidden behind a simple “verified” label." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-slate-700 bg-slate-800/60 p-5">
                <p className="text-sm font-semibold text-white">{c.t}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ============ PRODUCT EVIDENCE ============ */}
      <Section id="evidence" eyebrow="Real Product Evidence" title="See how PropLoX works"
        intro="Actual product screens — not concept mockups.">
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { img: "/evidence-report.png", alt: "PropLoX Title Scrutiny report — report particulars and schedule of property", cap: "Structured Title Scrutiny report (PDF)" },
            { img: "/evidence-findings.png", alt: "PropLoX report findings — cross-document flags with evidence", cap: "Findings & flags with document evidence" },
            { img: "/evidence-report-view.png", alt: "PropLoX on-screen verification report view", cap: "On-screen verification report" },
          ].map((e) => (
            <figure key={e.img} className="card-lift overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`evidence-${e.img.split("/").pop().split(".")[0]}`}>
              <img src={e.img} alt={e.alt} className="h-56 w-full object-cover object-top" />
              <figcaption className="px-4 py-3 text-xs font-medium text-slate-600">{e.cap}</figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400">What you see is based on the PropLoX product and workflow, not a concept mockup.</p>
      </Section>

      {/* ============ BUSINESS ============ */}
      <Section id="business" dark eyebrow="For Business" title="Manage property transactions across your team"
        intro="Whether you're managing buyers, legal reviews, property documents or registration processes, PropLoX is designed to keep the transaction workflow organized.">
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          {BUSINESS.map((b) => (
            <div key={b.name} className="rounded-xl border border-slate-700/80 bg-slate-800/40 p-6" data-testid={`business-card-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <b.icon className="h-6 w-6 text-blue-400" />
              <p className="mt-3 text-base font-semibold text-white">For {b.name}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{b.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button size="lg" data-testid="request-demo-button" onClick={() => setDialog({ mode: "business", service: "" })}>Request a Business Demo</Button>
          <Button size="lg" variant="outline" className="border-slate-600 bg-transparent text-white hover:bg-slate-800" data-testid="talk-to-proplox-button"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>Talk to PropLoX</Button>
        </div>
      </Section>

      {/* ============ CONTACT ============ */}
      <Section id="contact" eyebrow="Contact" title="Talk to PropLoX"
        intro="Have a question about a property transaction or one of our services? Tell us what you need and our team will guide you to the relevant next step.">
        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-5" data-testid="contact-details">
            <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-blue-600" /><a href="tel:+917338196493" className="text-sm font-medium text-slate-800 hover:text-blue-600" data-testid="contact-phone">+91 73381 96493</a></div>
            <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-blue-600" /><a href="mailto:support@proplox.com" className="text-sm font-medium text-slate-800 hover:text-blue-600" data-testid="contact-email">support@proplox.com</a></div>
            <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-blue-600" /><p className="text-sm font-medium text-slate-800" data-testid="contact-address">Sharavi Plaza, near Chethana Hotel,<br />Davanagere, Karnataka</p></div>
          </div>
          <form onSubmit={sendContact} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Name *</Label><Input required data-testid="contact-name-input" value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Phone *</Label><Input required data-testid="contact-phone-input" value={contact.phone} onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} placeholder="+91 …" /></div>
            </div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea rows={4} data-testid="contact-message-input" value={contact.message} onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))} placeholder="Tell us what you need…" /></div>
            <Button type="submit" disabled={sending} data-testid="contact-submit-button">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Contact PropLoX"}
            </Button>
          </form>
        </div>
      </Section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-blue-600 py-16 text-white lg:py-20" data-testid="final-cta">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Ready to take the next step?</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-blue-100 sm:text-base">
            Choose the property service you need and get started with PropLoX.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button size="lg" variant="secondary" data-testid="final-get-service-button" onClick={openService}>Get a Service</Button>
            <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="final-talk-button"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>Talk to PropLoX</Button>
          </div>
          <p className="mt-5 text-xs text-blue-200">From verification to completion, keep the important parts of your property transaction connected.</p>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-slate-950 py-14 text-slate-400">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 md:grid-cols-4 lg:px-8">
          <div>
            <Logo boxed height="h-10" />
            <p className="mt-4 text-sm leading-relaxed">PropLoX — property transaction services and workflow platform.</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Services</p>
            <ul className="mt-3 space-y-2 text-sm">{SERVICES.map((s) => <li key={s.id}><a href="/#services" className="hover:text-blue-400">{s.id}</a></li>)}</ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Business</p>
            <ul className="mt-3 space-y-2 text-sm">{BUSINESS.map((b) => <li key={b.name}><a href="/#business" className="hover:text-blue-400">{b.name}</a></li>)}</ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Company</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href="/#problem" className="hover:text-blue-400">About</a></li>
              <li><a href="/#contact" className="hover:text-blue-400">Contact</a></li>
              <li><Link to="/privacy" className="hover:text-blue-400" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-blue-400" data-testid="footer-terms-link">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-7xl border-t border-slate-800 px-4 pt-6 sm:px-6 lg:px-8">
          <p className="text-xs font-medium text-slate-300">Important Disclosure</p>
          <p className="mt-1 text-xs leading-relaxed">AI-assisted document analysis does not replace professional legal judgement or legal advice.</p>
          <p className="mt-3 text-xs">© PropLoX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
